/** @jsxRuntime automatic */
import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "./utils";
import { scrollFadeEdge } from "./scroll-fade";
import { useIndicator } from "./indicator";
import { noteHotAction } from "./motion";
import { rovingStep, rovingTabStop } from "./roving-tab-stop";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional 16 px icon before the label. */
  icon?: ReactNode;
  /**
   * Optional counter after the label ("Rechazadas 3"). It renders apart from
   * the label in tabular digits and never truncates: when the segment is too
   * narrow the label gets the ellipsis, the number stays.
   */
  count?: number;
  disabled?: boolean;
}

/**
 * - `hug` (default): the track is as wide as its segments (never wider than its container).
 * - `fill`: equal segments that share the container width; labels truncate,
 *   counters stay. The control can never be wider than its container.
 * - `scroll`: segments keep their natural width and the track scrolls
 *   sideways with a fade on the hidden edge (many or long options on a phone).
 */
export type SegmentedLayout = "hug" | "fill" | "scroll";

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  /** Accessible name of the group. */
  "aria-label": string;
  /** sm: 32 px with a mouse · md: 36 px. Both keep 44 px with a finger. */
  size?: "sm" | "md";
  /** How the segments use the width (see `SegmentedLayout`). */
  layout?: SegmentedLayout;
  /** @deprecated Use `layout="fill"`. */
  fullWidth?: boolean;
  className?: string;
}

/**
 * One segmented style for the whole product: muted track (a sunken well in
 * dark), a raised thumb outlined with the control border and a xs shadow that slides (200 ms --ease-move) to the selected segment. A radio
 * group: arrow keys move the selection. Labels never wrap; in `fill` they
 * truncate and in `scroll` the track scrolls, so the control never pushes the
 * page wider than the phone (visual audit 2026-10-13, applications-01).
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  "aria-label": ariaLabel,
  size = "md",
  layout: layoutProp,
  fullWidth = false,
  className,
}: SegmentedControlProps<T>) {
  const layout: SegmentedLayout = layoutProp ?? (fullWidth ? "fill" : "hug");
  const trackRef = useRef<HTMLDivElement | null>(null);
  // The thumb glides under the selected segment (useIndicator): never on its
  // first measure or a resize.
  const { ref: thumbRef } = useIndicator(trackRef, value);

  // Scroll layout: keep the selected segment in view and fade the hidden edges.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || layout !== "scroll") return;
    const updateFade = () => {
      const edge = scrollFadeEdge(track.scrollLeft, track.clientWidth, track.scrollWidth);
      if (edge) track.dataset.scrollFade = edge;
      else delete track.dataset.scrollFade;
    };
    const selected = track.querySelector<HTMLElement>('[aria-checked="true"]');
    if (selected) {
      const start = selected.offsetLeft;
      const end = start + selected.offsetWidth;
      if (start < track.scrollLeft) track.scrollLeft = start;
      else if (end > track.scrollLeft + track.clientWidth) track.scrollLeft = end - track.clientWidth;
    }
    updateFade();
    track.addEventListener("scroll", updateFade, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateFade);
    observer?.observe(track);
    return () => {
      track.removeEventListener("scroll", updateFade);
      observer?.disconnect();
    };
  }, [layout, value, options]);

  // One Tab stop (rovingTabStop, shared with ChipGroup): the selected segment,
  // or the first enabled one when `value` is not among the options (a view
  // whose option went away) or is disabled; the group never leaves the Tab order.
  const tabStop = rovingTabStop(options, value);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"];
    if (!keys.includes(event.key) || tabStop < 0) return;
    event.preventDefault();
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
    // From the focused segment (the Tab stop when the selection is not on screen).
    const focused = (event.target as Element).closest<HTMLElement>("[data-value]")?.dataset.value;
    const from = options.findIndex((o) => o.value === focused);
    const next = options[rovingStep(options, from >= 0 ? from : tabStop, step)];
    if (!next) return;
    // Focus first: a change it causes starts at the control the person moved
    // to (the theme's circle grows from the new segment, spec 07 › Movimiento).
    trackRef.current?.querySelector<HTMLElement>(`[data-value="${CSS.escape(next.value)}"]`)?.focus();
    // A held arrow or segments in quick succession: the content below changes at once (hot mode).
    noteHotAction(event);
    onValueChange(next.value);
  };

  return (
    <div
      ref={trackRef}
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      data-layout={layout}
      className={cn(
        "relative isolate inline-flex min-w-0 max-w-full items-stretch rounded-md bg-muted p-0.5 dark:bg-black/30 dark:ring-1 dark:ring-inset dark:ring-white/6",
        layout === "fill" && "flex w-full",
        layout === "scroll" &&
          "scroll-fade-x flex w-full snap-x snap-proximity overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <span
        ref={thumbRef}
        aria-hidden="true"
        // The thumb's edge is the 3:1 control border (components-06): the
        // selected segment reads in both themes without reading the label.
        // `ui-indicator` places it; useIndicator glides it on the compositor (200 ms on --ease-move).
        className="ui-indicator absolute inset-y-0.5 left-0 -z-10 rounded-[calc(var(--radius-md)-2px)] bg-segmented-thumb shadow-xs ring-1 ring-inset ring-input dark:shadow-[var(--highlight-top)]"
      />
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            data-value={option.value}
            data-indicator-key={option.value}
            tabIndex={index === tabStop ? 0 : -1}
            disabled={option.disabled}
            onClick={() => {
              if (!selected) noteHotAction();
              onValueChange(option.value);
            }}
            className={cn(
              "inline-flex min-h-11 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[calc(var(--radius-md)-2px)] text-label font-medium transition-colors duration-base ease-move hover:duration-fast focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed [&_svg]:size-4 [&_svg]:shrink-0",
              size === "sm" ? "md:pointer-fine:min-h-7" : "md:pointer-fine:min-h-8",
              layout === "fill" ? "flex-1 basis-0 px-2" : "px-3",
              layout === "scroll" && "shrink-0 snap-start",
              // The label's color travels with the thumb (200 --ease-move): the new one
              // inks as the thumb arrives and the old one dims as it leaves, never ahead
              // of it; a mouse's hover tints in 120.
              // Disabled (components-16): the chosen segment keeps a readable
              // muted label on its thumb, the others go to the disabled grey,
              // so the choice still reads without the thumb.
              selected
                ? "text-foreground disabled:text-muted-foreground"
                : "text-muted-foreground enabled:hover:text-foreground disabled:text-disabled-foreground",
            )}
          >
            {option.icon}
            <span className="min-w-0 truncate">{option.label}</span>
            {/* The space keeps the accessible name "Rechazadas 3", not "Rechazadas3"
                (a whitespace node is not a flex item, so the layout ignores it). */}
            {option.count != null ? " " : null}
            {option.count != null ? (
              <span className={cn("num shrink-0 text-caption transition-colors duration-base ease-move", selected ? "text-muted-foreground" : "text-subtle-foreground")}>
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
